import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';

import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import Dialog from 'Components/UI/Dialog';
import Button from 'Components/UI/Button';
import GenericError from 'Errors/GenericError';
import useMetaFormBuilder from 'Hooks/useMetaFormBuilder';

import useMobileLayout from 'Hooks/useMobileLayout';
import MetaFormFieldList from './Components/MetaFormFieldList';
import MetaLayout from './Components/MetaLayout';

const styles = theme => ({
  root: {},
  dialogPaper: {},
  dialogPaperDesktop: {
    minWidth: theme.dimensions.md,
  },
  dialogTitle: {},
  title: {},
  hideTitle: {
    display: 'none',
  },
  content: {},
  fieldList: {},
  actions: {},
  cancelButton: {},
  saveButton: {},
});

function MetaCollectionFormDialog({
  inlineSubmit,
  layout,
  className,
  classes,
  collection,
  onDelete: onDeleteProp,
  onFailure,
  onSubmit: onSubmitProp,
  onClose,
  rows,
  title,
  dialogText,
  readonly,
  deleteMessage,
  additionalActions: additionalActionsProp,
  showSubmitButton: showSubmitButtonProp,
  LayoutProps,
  onAdd,
  onRemove,
  SubmitButtonProps,
  disableBackdropClickClosure,
  metaOverrideSet,
  test,
}) {
  if (collection.resources().length() !== 1) {
    throw new GenericError(
      'invalid-resource-count',
      'MetaCollectionFormDialog is limited to single resource collections for now.',
    );
  }

  const [
    showDeleteConfirmationDialog,
    setShowDeleteConfirmationDialog,
  ] = useState(false);
  const isMobile = useMobileLayout();
  const [confirmationDialogData, setConfirmationDialogData] = useState(null);

  const {
    eventTrackingNodeRef,
    resource,
    onChange,
    onDelete,
    onSubmit,
    additionalActions: wrappedAdditionalActions,
    saving,
    errors,
  } = useMetaFormBuilder({
    onDelete: onDeleteProp,
    onFailure,
    onSubmit: onSubmitProp,
    additionalActions: additionalActionsProp,
    resource: collection.resources().head(),
    inlineSubmit,
  });

  const actions = [];
  let showSubmitButton = showSubmitButtonProp && !readonly;
  if (!readonly) {
    if (
      resource
        .meta()
        .template()
        .list()
        .every(v => v.attr('hidden') || v.attr('readonly'))
    ) {
      showSubmitButton = false;
    }
  }

  const additionalActions = [...wrappedAdditionalActions];

  if (onDelete) {
    additionalActions.unshift({
      component: () => {
        return (
          <Button
            onClick={() => setShowDeleteConfirmationDialog(true)}
            color="red"
            variant="contained"
          >
            Delete
          </Button>
        );
      },
    });
  }

  return (
    <>
      <Dialog
        actions={actions}
        classes={{
          root: classNames(classes.root, className),
          paper: classNames(classes.dialogPaper, {
            [classes.dialogPaperDesktop]: !isMobile,
          }),
        }}
        fullScreen={isMobile}
        open
        disableRestoreFocus
        disableEnforceFocus
        onClose={(event, reason) => {
          if (disableBackdropClickClosure) {
            if (reason !== 'backdropClick') { // <--- Prevents closure
              onClose();
            }
          } else {
            onClose();
          }
        }}
        showCloseIcon={typeof title === 'string'}
        ref={eventTrackingNodeRef}
        test={test}
      >
        <DialogTitle className={classNames(classes.title, {
          [classes.hideTitle]: !title,
        })}
        >
          {typeof title === 'function' ? title(resource) : title}
        </DialogTitle>
        <DialogContent className={classes.content}>
          {dialogText && <p>{dialogText}</p>}
          {!layout ? (
            <MetaFormFieldList
              className={classes.fieldList}
              errors={errors}
              onChange={onChange}
              resource={resource}
              rows={rows}
              metaOverrideSet={metaOverrideSet}
            />
          ) : (
            <MetaLayout
              metaOverrideSet={metaOverrideSet}
              resource={resource}
              collection={collection}
              layout={layout}
              LayoutProps={LayoutProps}
              errors={errors}
              onChange={onChange}
              onSubmit={onSubmit}
              onAdd={onAdd}
              onRemove={onRemove}
            />
          )}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            className={classes.cancelButton}
            color="secondary"
            variant="text"
            onClick={onClose}
          >
            {'Cancel'}
          </Button>
          {additionalActions.map((action) => {
            const {
              component,
              onClick,
              confirmationMessage,
              actionText,
              closeOnSuccess = false,
            } = action;

            if (component) {
              return component(resource, onClose);
            }

            if (onClick) {
              return (
                <Button
                  test={action.test}
                  color="primary"
                  variant="contained"
                  disabled={saving}
                  onClick={
                    confirmationMessage
                      ? () => setConfirmationDialogData({
                        confirmationMessage,
                        onConfirm: onClick,
                        closeOnSuccess,
                      })
                      : () => onClick(resource, onClose)
                  }
                >
                  {actionText}
                </Button>
              );
            }
            return null;
          })}
          {showSubmitButton && !inlineSubmit && (
            <Button
              className={classes.saveButton}
              color="secondary"
              disabled={saving || !resource.isValid()}
              disableElevation
              onClick={onSubmit}
              variant="contained"
              {...SubmitButtonProps}
            >
              {saving ? 'Saving' : 'Submit'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {confirmationDialogData && (
        <ConfirmationDialog
          onCancel={() => {
            setConfirmationDialogData(null);
          }}
          title="Are you sure?"
          message={confirmationDialogData.confirmationMessage || 'Continue?'}
          onConfirm={() => {
            setConfirmationDialogData(null);
            confirmationDialogData.onConfirm(resource, onClose);
          }}
          showCloseIcon={false}
        />
      )}
      {showDeleteConfirmationDialog && (
        <ConfirmationDialog
          onCancel={() => {
            setShowDeleteConfirmationDialog(false);
          }}
          title="Are you sure?"
          message={
            deleteMessage
            || 'This will permanently delete the selected resource.'
          }
          onConfirm={() => {
            setShowDeleteConfirmationDialog(false);
            onDelete();
          }}
          showCloseIcon={false}
        />
      )}
    </>
  );
}

MetaCollectionFormDialog.defaultProps = {
  className: '',
  onDelete: null,
  onFailure: null,
  onSubmit: null,
  rows: [],
  readonly: false,
  layout: null,
  additionalActions: [],
  showSubmitButton: true,
  inlineSubmit: false,
  LayoutProps: {},
  onAdd: null,
  onRemove: null,
  SubmitButtonProps: {},
  disableBackdropClickClosure: false,
  metaOverrideSet: null,
  test: 'meta-collection-form-dialog',
};

MetaCollectionFormDialog.propTypes = {
  className: PropTypes.string,
  test: PropTypes.string,
  classes: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onFailure: PropTypes.func,
  onSubmit: PropTypes.func,
  rows: PropTypes.array,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func])
    .isRequired,
  dialogText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  readonly: PropTypes.bool,
  layout: PropTypes.any,
  additionalActions: PropTypes.arrayOf(
    PropTypes.shape({
      confirmationMessage: PropTypes.string,
      component: PropTypes.node,
      onClick: PropTypes.func,
    }),
  ),
  showSubmitButton: PropTypes.bool,
  inlineSubmit: PropTypes.bool,
  LayoutProps: PropTypes.object,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  SubmitButtonProps: PropTypes.object,
  disableBackdropClickClosure: PropTypes.bool,
  metaOverrideSet: PropTypes.object,
};

export default withStyles(styles)(MetaCollectionFormDialog);
